import type { GridGetRowsParams } from "@mui/x-data-grid-pro";
import { useMemo } from "react";
import type { AnalyticsMoment } from "../types";
import { SupabaseGroupedMomentAnalyticsDataService } from "../../../../modules/stats/server/analytics-service";
import { useSupabaseCallback } from "../../../../server/supabase/hooks";
import { toISO8601 } from "../../../../utils/iso8601";
import { type TransformedGroupedMomentAnalytics, transformGroupedMomentAnalytics } from "./utils";

export interface TypedGridDataSource<T extends TransformedGroupedMomentAnalytics> {
  getRows(params: GridGetRowsParams): Promise<{
    rows: T[];
    rowCount?: number;
    pageInfo?: {
      hasNextPage?: boolean;
      nextCursor?: string;
    };
  }>;
}

export function useGroupedMomentAnalyticsDataSource({
  id,
}: Pick<AnalyticsMoment, "id">): TypedGridDataSource<TransformedGroupedMomentAnalytics> {
  const loader = useSupabaseCallback(
    async (
      { supabase, account_id },
      { start, end, sortModel, filterModel }: GridGetRowsParams,
    ): Promise<{
      rows: TransformedGroupedMomentAnalytics[];
      rowCount: number;
    }> => {
      // Throw error if start is not a number
      if (typeof start !== "number") throw new Error("Invalid start value");

      const service = new SupabaseGroupedMomentAnalyticsDataService(supabase);
      const query = service.client
        .from(service.table)
        .select("*", { count: "estimated" })
        .eq("moment_id", id)
        .eq("account_id", account_id);

      // We only allow custom filtering by min_timestamp, and using "AND" logic
      filterModel.items.forEach((filter) => {
        if (filter.field !== "min_timestamp") return;

        if (!filter.value) return;

        // If it's not a Date, we don't want to filter
        if (!(filter.value instanceof Date)) return;

        if (filter.operator === "after") {
          query.gt("min_timestamp", toISO8601(filter.value));
        }

        if (filter.operator === "before") {
          query.lt("min_timestamp", toISO8601(filter.value));
        }
      });

      sortModel.forEach((sort) => {
        query.order(sort.field, { ascending: sort.sort === "asc" });
      });

      const resp = await query.range(start, end);

      if (resp.error) {
        throw new Error("Unable to load Communication stats");
      }

      if (!resp.data) return { rows: [], rowCount: 0 };

      const rows = resp.data.map((d, i) => transformGroupedMomentAnalytics(d, i));

      return {
        rows,
        rowCount: resp.count ?? resp.data.length,
      };
    },
    [id],
  );

  const fn: TypedGridDataSource<TransformedGroupedMomentAnalytics> = useMemo(
    () => ({
      getRows: async (params) => {
        const resp = await loader(params);

        if (!resp) throw new Error("Unable to load Communication stats");

        return resp;
      },
    }),
    [loader],
  );

  return fn;
}
